import { useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useCV } from '../hooks/useCV'
import { companyDefs, parsePeriod } from '../utils/period'
import Section from './Section'

const Skills = () => {
  const { skillCategories, experiences } = useCV()
  const [company, setCompany] = useState<string | null>(null)
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 })

  // Companies with their time range, taken from the experience entries
  const companies = useMemo(
    () =>
      companyDefs.flatMap((def) => {
        const exp = experiences.find((e) => def.match.test(e.company))
        if (!exp) return []
        const { start, end } = parsePeriod(exp.period)
        const from = Math.floor(start)
        const to = Math.floor(end)
        const nowYear = new Date().getFullYear()
        const range = to >= nowYear ? `${from} - now` : from === to ? `${from}` : `${from} - ${to}`
        return [{ ...def, start, end, range }]
      }),
    [experiences],
  )

  const min = Math.floor(Math.min(...companies.map((c) => c.start)))
  const max = Math.ceil(Math.max(...companies.map((c) => c.end)))
  const span = max - min
  const years = Array.from({ length: span + 1 }, (_, i) => min + i)
  const left = (v: number) => `${((v - min) / span) * 100}%`
  const width = (a: number, b: number) => `${((b - a) / span) * 100}%`

  // Chart rows: one per skill, grouped by category, oldest first so bars form a staircase
  const groups = useMemo(
    () =>
      skillCategories
        .map((category) => {
          const rows = category.skills
            .filter((s) => s.at?.length)
            .map((skill) => {
              const segments = (skill.at ?? [])
                .map((id) => companies.find((c) => c.id === id))
                .filter((c): c is (typeof companies)[number] => Boolean(c))
              return {
                name: skill.name,
                segments,
                first: Math.min(...segments.map((s) => s.start)),
                total: segments.reduce((sum, s) => sum + (s.end - s.start), 0),
              }
            })
            .filter((r) => r.segments.length > 0)
            .sort((a, b) => a.first - b.first || b.total - a.total)
          return { id: category.id, name: category.name, rows }
        })
        .filter((g) => g.rows.length > 0),
    [skillCategories, companies],
  )

  let rowIndex = 0

  return (
    <Section id="skills" title="Skills" note="Everything I work with, then when and where I used each skill.">
      {/* Every skill in plain text so a quick scan (or Ctrl+F) finds all of them */}
      <dl>
        {skillCategories.map((category) => (
          <div key={category.id} className="grid sm:grid-cols-[10rem_1fr] gap-x-6 gap-y-1 py-4 border-b border-rule first:pt-0">
            <dt className="font-serif text-lg font-medium">{category.name}</dt>
            <dd className="text-base leading-relaxed text-mute">
              {category.skills.map((skill) => skill.name).join(', ')}
            </dd>
          </div>
        ))}
      </dl>

      <h3 className="mt-16 font-serif text-2xl font-medium">Skills over time</h3>
      <p className="mt-1 text-sm text-mute max-w-prose">
        Each bar is a stretch of time I used a skill at a job. Select a company to highlight it.
      </p>

      {/* Company filter */}
      <div className="mt-5 flex flex-wrap gap-2">
        {companies.map((c) => (
          <button
            key={c.id}
            type="button"
            aria-pressed={company === c.id}
            onClick={() => setCompany(company === c.id ? null : c.id)}
            className={`px-3 py-1.5 text-left text-sm transition-colors ${
              company === c.id ? 'bg-ink text-paper' : 'border border-rule hover:bg-rule/40'
            }`}
          >
            <span className="font-medium">{c.label}</span>
            <span className={`ml-2 ${company === c.id ? 'text-paper/70' : 'text-mute'}`}>{c.range}</span>
          </button>
        ))}
      </div>

      <div ref={ref} className="relative mt-6 pl-28 sm:pl-44">
        {/* Background: company periods and year lines, aligned with the bar tracks */}
        <div aria-hidden className="pointer-events-none absolute inset-y-0 left-28 sm:left-44 right-0">
          {companies.map((c) => (
            <div
              key={c.id}
              style={{ left: left(c.start), width: width(c.start, c.end) }}
              className={`absolute inset-y-0 transition-colors ${company === c.id ? 'bg-accent/15' : 'bg-ink/[0.035]'}`}
            />
          ))}
          {years.map((y) => (
            <div key={y} style={{ left: left(y) }} className="absolute inset-y-0 w-px bg-rule/70" />
          ))}
        </div>

        {/* Year axis, stays visible while scrolling the chart */}
        <div className="sticky top-[5.05rem] sm:top-16 z-10 -ml-28 sm:-ml-44 pl-28 sm:pl-44 bg-paper/95 backdrop-blur h-7 border-b border-rule" aria-hidden>
          <div className="relative h-full text-[0.6875rem] text-mute">
            {years.map((y) => (
              <span
                key={y}
                style={{ left: left(y) }}
                className={`absolute top-1.5 -translate-x-1/2 ${(y - min) % 2 ? 'hidden sm:block' : ''}`}
              >
                {y}
              </span>
            ))}
          </div>
        </div>

        {groups.map((group) => (
          <div key={group.id} className="relative mt-6 first-of-type:mt-3">
            <h4 className="-ml-28 sm:-ml-44 mb-1 font-serif text-lg font-medium">{group.name}</h4>
            <ul>
              {group.rows.map((row) => {
                const index = rowIndex++
                return (
                  <li
                    key={row.name}
                    className="group relative h-7 hover:bg-ink/5"
                  >
                    <span
                      title={row.name}
                      className="absolute inset-y-0 right-full flex w-28 sm:w-44 items-center pr-2 text-[0.8125rem] sm:text-[0.9375rem]"
                    >
                      <span className="truncate">{row.name}</span>
                    </span>
                    <span className="sr-only">Used at {row.segments.map((s) => s.label).join(', ')}</span>
                    <div aria-hidden className="absolute inset-0">
                      {row.segments.map((seg) => (
                        <span
                          key={seg.id}
                          title={`${row.name} at ${seg.label}`}
                          style={{
                            left: left(seg.start),
                            width: width(seg.start, seg.end),
                            transform: inView ? 'scaleX(1)' : 'scaleX(0)',
                            transitionDelay: `${index * 18}ms`,
                          }}
                          className={`absolute top-2 bottom-2 min-w-[4px] origin-left bg-accent transition-[transform,opacity] duration-700 motion-reduce:transition-none ${
                            company && company !== seg.id ? 'opacity-20' : 'opacity-100'
                          }`}
                        />
                      ))}
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </div>
    </Section>
  )
}

export default Skills
